import { TranslationSupportedLanguage } from "@amzn/it-support-connect-api-model";

import { translateMessage } from "../../connect-api";
import { CcpManager } from "../../connect/ccp-manager";
import { TrackedEvent, trackEvent } from "../../snowplow";

enum TranslationDisableReason {
  IneligibleContact = "IneligibleContact",
  SameLanguageAgent = "SameLanguageAgent",
  NoPreferredLanguage = "NoPreferredLanguage",
  DisabledMidChat = "DisabledMidChat",
}

export class Translation {
  /**
   * For now, only agents that speaks English will have access to the feature.
   */
  public static readonly DEFAULT_AGENT_CHAT_LANGUAGE = "en";
  /**
   * Translation is only supported for the following languages.
   * The key is the language code used by Connect API.
   * The value is the language code used by Amazon Translate.
   */
  private static readonly SUPPORTED_LANGUAGE_CODES: Record<
    string,
    TranslationSupportedLanguage
  > = {
    fra: "fr",
    deu: "de",
    ita: "it",
    por: "pt",
    spa: "es",
    // The following languages are available on IT Portal/FirstAid but they are not approved for translation.
    jpn: "ja",
    // zho: "zh",
    // kor: "ko",
  };

  /**
   * Translation is automatically enabled for the following languages.
   * The key is the country code used by Connect API.
   * The value is the language code used by Amazon Translate.
   */
  private static readonly SUPPORTED_COUNTRY_CODES: Record<
    string,
    TranslationSupportedLanguage
  > = {
    jpn: "ja",
  };

  private static readonly TRANSLATION_ON_MESSAGE =
    "Your messages may be machine translated to help you get faster support. We'd love to hear your feedback afterward so we can keep making this feature better!";
  private static readonly TRANSLATION_OFF_MESSAGE =
    "Translation service has been disabled. Messages will now appear in their original language.";

  private static readonly singleton = new Translation();
  /**
   * agentKnownLanguages is a list of languages that the agent understands.
   */
  private agentKnownLanguages: Set<string> = new Set();
  private initialized = false;
  private translatedContactId = new Set<string>();
  private agentUsername: string | undefined = undefined;

  public static get instance(): Translation {
    return Translation.singleton;
  }

  /**
   * This function sets up a message listener that captures outgoing agent message, translates the content, and sends out the translation in an application/json message.
   */
  public initTranslation(agent: connect.Agent): boolean {
    // do nothing translation has been initialized
    if (this.initialized) {
      return true;
    }

    this.initialized = true;

    try {
      // overriding the logic for this one routing profile as the agents do not speak japanese
      if (agent.getRoutingProfile().name !== "ITS_GSD1_jpn_pilot") {
        agent
          .getRoutingProfile()
          .name.toLowerCase()
          .split("_")
          .forEach(
            (chunk) =>
              !!Translation.SUPPORTED_LANGUAGE_CODES[chunk] &&
              this.agentKnownLanguages.add(chunk)
          );
      }
      this.agentUsername = agent.getConfiguration().username;

      CcpManager.instance.onChatConnected(
        ({ connectedContact, chatSession }) => {
          // check if contact is eligible for translation
          if (!this.isContactEligible(connectedContact)) {
            trackEvent({
              elementId: TrackedEvent.TranslationDisabled,
              elementTarget: connectedContact.contactId,
              targetUrl: window.location.host,
              elementClasses: [TranslationDisableReason.IneligibleContact],
            });
            return;
          }

          // check if translation is enabled for the contact
          if (!this.isTranslationEnabledForContact(connectedContact)) {
            trackEvent({
              elementId: TrackedEvent.TranslationDisabled,
              elementTarget: connectedContact.contactId,
              targetUrl: window.location.host,
              elementClasses: [TranslationDisableReason.SameLanguageAgent],
            });
            return;
          }

          // Translation is enabled for all agents, let customer client know
          this.enableTranslationForContact(connectedContact, chatSession, true);
        }
      );

      // Here we set up the translation logic to translate agent messages when accepting the contact,
      // because Omnia sends a greeting when agent accepts the contact, and the greeting should be translated.
      // Ideally, this should be in onConnected.
      CcpManager.instance.onChatAccepted(({ acceptedContact, chatSession }) => {
        // check if contact is eligible for translation
        if (!this.isContactEligible(acceptedContact)) {
          return;
        }

        // check if translation is enabled for the contact
        if (!this.isTranslationEnabledForContact(acceptedContact)) {
          return;
        }

        // The event listener still needs to be set up since the agent can turn translation on during the contact.
        chatSession.onMessage((message: connect.ChatMessageEvent) => {
          const translate = async (retry = 0) => {
            try {
              // Get translation target language from either country or language attributes
              const contactAttributes = acceptedContact.getAttributes();
              const contactLanguage = contactAttributes["language"]?.value;
              const contactCountry = contactAttributes[
                "customerCountry"
              ]?.value?.toLowerCase();
              const routeJpnContactsByCountry =
                contactAttributes["routeJpnContactsByCountry"]?.value;

              // Only use country code for translation target language if routeJpnContactsByCountry === "true"
              const shouldUseCountryCode = routeJpnContactsByCountry === "true";

              const translationTargetLanguageCode =
                (shouldUseCountryCode &&
                  contactCountry &&
                  Translation.SUPPORTED_COUNTRY_CODES[contactCountry]) ||
                (contactLanguage &&
                  Translation.SUPPORTED_LANGUAGE_CODES[contactLanguage]);

              if (!translationTargetLanguageCode) {
                throw new Error("No translation target language found");
              }

              const messageData = message.data;
              // capture an outgoing agent message
              // Only the messages that are actually sent by the agent need to be translated,
              // and all of those messages have the ContentType "text/plain"
              if (
                messageData.Id &&
                messageData.Content &&
                messageData.ParticipantRole === "AGENT" &&
                messageData.ContentType === "text/plain" &&
                messageData.Type === "MESSAGE"
              ) {
                // translate the content for the original message
                const translateResponse = await translateMessage({
                  Text: messageData.Content,
                  SourceLanguageCode: Translation.DEFAULT_AGENT_CHAT_LANGUAGE,
                  TargetLanguageCode: translationTargetLanguageCode,
                  ContactId:
                    acceptedContact.getOriginalContactId() ??
                    acceptedContact.contactId,
                });

                if (!translateResponse.TranslatedText) {
                  throw new Error(JSON.stringify(translateResponse));
                }

                // construct the translation message
                const translationMessage: StructuredChatMessage = {
                  type: "Translation",
                  content: {
                    messageId: messageData.Id,
                    translatedText: translateResponse.TranslatedText,
                    sourceLanguageCode: Translation.DEFAULT_AGENT_CHAT_LANGUAGE,
                    targetLanguageCode: translationTargetLanguageCode,
                  },
                };
                // send the translation in an application/json message
                await chatSession.sendMessage({
                  contentType: "application/json",
                  message: JSON.stringify(translationMessage),
                });
              } else if (
                messageData.Content &&
                messageData.ParticipantRole === "AGENT" &&
                messageData.ContentType === "application/json" &&
                messageData.Type === "MESSAGE"
              ) {
                const message = JSON.parse(
                  messageData.Content
                ) as StructuredChatMessage;
                if (
                  message.type === "TranslationToggle" &&
                  message.content.language
                ) {
                  this.translatedContactId.add(acceptedContact.getContactId());
                }
              }
            } catch (err) {
              console.error("Could not send translation", err);
              trackEvent({
                elementId: TrackedEvent.FailedTranslation,
                elementTarget: JSON.stringify(err),
                targetUrl: acceptedContact.getContactId(),
                elementClasses: [message.data.Content ?? ""],
              });
              if (retry < 1) {
                void translate(retry + 1);
              }
            }
          };
          void translate();
        });
      });

      trackEvent({
        elementId: TrackedEvent.TranslationInitialized,
        elementTarget: this.agentUsername,
        targetUrl: window.location.host,
        elementClasses: [Translation.DEFAULT_AGENT_CHAT_LANGUAGE],
      });
      return true;
    } catch (err) {
      console.error(err);
      trackEvent({
        elementId: TrackedEvent.TranslationInitializationFailed,
        elementTarget: this.agentUsername,
        targetUrl: window.location.host,
        elementClasses: [JSON.stringify(err)],
      });
      return false;
    }
  }

  /**
   * This function enables tracking translated chats.
   */
  public trackTranslatedChats(): void {
    CcpManager.instance.onChatAccepted(({ acceptedContact, chatSession }) => {
      // check if contact is eligible for translation
      if (!this.isContactEligible(acceptedContact)) {
        return;
      }

      chatSession.onMessage((message: connect.ChatMessageEvent) => {
        const messageData = message.data;
        // capture TranslationToggle message and record the contact id
        if (
          messageData.Content &&
          messageData.ParticipantRole === "AGENT" &&
          messageData.ContentType === "application/json" &&
          messageData.Type === "MESSAGE"
        ) {
          const message = JSON.parse(
            messageData.Content
          ) as StructuredChatMessage;
          if (
            message.type === "TranslationToggle" &&
            message.content.language
          ) {
            this.translatedContactId.add(acceptedContact.getContactId());
          }
        }
      });
    });
  }

  /**
   * This function tells if the given contact is eligible for translation.
   * @param contact the contact
   * @returns a boolean that indicates if translation is enabled for the contact
   */
  private isContactEligible(contact: connect.Contact): boolean {
    const contactAttributes = contact.getAttributes();
    const contactLanguage = contactAttributes["language"]?.value;
    const contactCountry = contactAttributes[
      "customerCountry"
    ]?.value?.toLowerCase();
    const isTranslationAvailable =
      contactAttributes["isTranslationAvailable"]?.value;
    const translationTargetLanguageCode = contactLanguage
      ? Translation.SUPPORTED_LANGUAGE_CODES[contactLanguage]
      : undefined;
    const translationTargetCountryCode = contactCountry
      ? Translation.SUPPORTED_COUNTRY_CODES[contactCountry]
      : undefined;

    // For Japanese contacts, check if Japanese translation is explicitly enabled
    if (this.isJapaneseContact(contactLanguage, contactCountry)) {
      const japaneseTranslateEnabled =
        contactAttributes["japaneseTranslateEnabled"]?.value;
      if (japaneseTranslateEnabled !== "true") {
        return false;
      }
    }

    // check:
    // 1. if customer language is not English and supported by translation
    // 2. the source app for the contact is supported
    // 3. the customer is using the new customer client
    return (
      contact.getType() === connect.ContactType.CHAT &&
      (!!translationTargetLanguageCode ||
        (contactAttributes["routeJpnContactsByCountry"]?.value === "true" &&
          !!translationTargetCountryCode)) &&
      isTranslationAvailable === "TRUE"
    );
  }

  /**
   * This function enables or disables translation for the given contact.
   * @param contact the contact
   * @param chatSession the chat session for the contact
   * @param enabled a boolean that indicates if translation is enabled or disabled for the contact
   */
  private enableTranslationForContact(
    contact: connect.Contact,
    chatSession: connect.ChatSession,
    enabled: boolean
  ): void {
    const contactAttributes = contact.getAttributes();
    const customerCountry = contactAttributes[
      "customerCountry"
    ]?.value?.toLowerCase();
    const routeJpnContactsByCountry =
      contactAttributes["routeJpnContactsByCountry"]?.value;

    // Only use country code for translation target language if routeJpnContactsByCountry === "true"
    const shouldUseCountryCode = routeJpnContactsByCountry === "true";
    const countryLanguage =
      shouldUseCountryCode && customerCountry
        ? Translation.SUPPORTED_COUNTRY_CODES[customerCountry]
        : undefined;

    const translationToggleMessage: StructuredChatMessage = {
      type: "TranslationToggle",
      content: {
        isEnabled: enabled,
        language: enabled
          ? countryLanguage || contactAttributes["language"]?.value
          : undefined,
      },
    };
    void chatSession.sendMessage({
      contentType: "application/json",
      message: JSON.stringify(translationToggleMessage),
    });
    void chatSession.sendMessage({
      contentType: "text/plain",
      message: enabled
        ? Translation.TRANSLATION_ON_MESSAGE
        : Translation.TRANSLATION_OFF_MESSAGE,
    });

    trackEvent({
      elementId: enabled
        ? TrackedEvent.TranslationEnabled
        : TrackedEvent.TranslationDisabled,
      elementTarget: contact.contactId,
      targetUrl: window.location.host,
      elementClasses: enabled
        ? undefined
        : [TranslationDisableReason.DisabledMidChat],
    });
  }

  /**
   * This function tells if the specified contact is translated.
   * @param contactId the contactId
   * @returns a boolean that indicates if the contact has been translated
   */
  public isContactTranslated(contactId: string): boolean {
    return this.translatedContactId.has(contactId);
  }

  /**
   * This function tells if translation is enabled for the given contact.
   * @param contact the contact
   * @returns a boolean that indicates if translation is enabled for the contact
   */
  private isTranslationEnabledForContact(contact: connect.Contact): boolean {
    const contactAttributes = contact.getAttributes();
    const contactLanguage = contactAttributes["language"]?.value;
    const contactCountry = contactAttributes[
      "customerCountry"
    ]?.value?.toLowerCase();

    // For Japanese contacts, check if Japanese translation is explicitly enabled
    if (this.isJapaneseContact(contactLanguage, contactCountry)) {
      const japaneseTranslateEnabled =
        contactAttributes["japaneseTranslateEnabled"]?.value;
      if (japaneseTranslateEnabled !== "true") {
        return false;
      }
    }

    // Check if we have either language or country support
    const hasLanguageSupport =
      !!contactLanguage && !this.agentKnownLanguages.has(contactLanguage);
    const hasCountrySupport =
      !!contactCountry && !!Translation.SUPPORTED_COUNTRY_CODES[contactCountry];

    return hasLanguageSupport || hasCountrySupport;
  }

  /**
   * Helper method to determine if a contact is Japanese (either by language or country).
   * @param contactLanguage the contact's language code
   * @param contactCountry the contact's country code (already lowercased)
   * @returns true if the contact is Japanese
   */
  private isJapaneseContact(
    contactLanguage?: string,
    contactCountry?: string
  ): boolean {
    return contactLanguage === "jpn" || contactCountry === "jpn";
  }
}
